import React, { useState } from 'react';
import './PancreaticCalculator.css';
import Results from './Results';

const QUESTIONS = {
  symptoms: [
    { 
      id: 'Q1', 
      text: 'Abdominal distension', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q2', 
      text: 'Anorexia or early satiety', 
      defaultTier: 3,
      recommendations: ['Wait no longer than 6 weeks to investigate the pancreas in patients with persistent anorexia, early satiety, or change in dietary preferences, for which other causes such as psychological/social or change in medication have been eliminated.'],
      dependencies: {
        tier1: {
          groups: [
            {
              ids: ['Q16', 'Q11.4', 'Q11.3'],
              requiredCount: 1,
              defaultTier: 2,
              dependencyRecommendations: [
                'Suggested bloods: lipase, E/LFTs, FBC, coeliac serology',
                'If symptoms persist and other causes not identified: IV contrast abdominal CT'
              ]
            }
          ],
          allGroupsRequired: false
        }
      }
    },
    { 
      id: 'Q3', 
      text: 'Bloating', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true,
        }
      }
    },
    { 
      id: 'Q4', 
      text: 'Diarrhoea', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q5', 
      text: 'Diabetes mellitus', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      subOptions: [
        {
          id: 'Q5.1',
          text: 'New onset',
          defaultTier: 4,
          recommendations: [],
          dependencies: {
            tier1: {
              groups: [
                {
                  // Case 1: Age 40+ AND one of the high-risk conditions
                  ids: ['Q15'], // Age 40+
                  requiredCount: 1,
                  subGroup: {
                    ids: [
                      'Q16', // Family history of pancreatic cancer
                      'Q19', // History of pancreatitis
                      'Q11.3', // Persistent non-musculoskeletal upper back pain
                      'Q11.4', // Non-specific abdominal pain
                    ],
                    requiredCount: 1
                  },
                  defaultTier: 1,
                  dependencyRecommendations: [
                    'Suggested bloods: lipase, E/LFTs, FBC,',
                    'IV contrast CT (preferably pancreatic protocol)',
                    'Refer to gastroenterologist or surgeon'
                  ]
                },
                {
                  // Case 2: Age 40+ only
                  ids: ['Q15'], // Age 40+
                  requiredCount: 1,
                  subGroup: {
                    ids: [
                      'Q18', // smoking
                      'Q17', // drinking
                    ],
                    requiredCount: 1
                  },
                  defaultTier: 2,
                  dependencyRecommendations: [
                    'Trial diabetes management for 6 weeks',
                    'If DM not adequately controlled, consider IV contrast abdominal CT (preferably pancreatic protocol)'
                  ]
                }
              ],
              allGroupsRequired: false // We only need one case to be true
            }
          }
        },
        {
          id: 'Q5.2',
          text: 'Existing and has become newly unstable',
          defaultTier: 4,
          recommendations: [],
          dependencies: {
            tier1: {
              groups: [
                {
                  // Case 1: Age 40+ AND one of the high-risk conditions
                  ids: ['Q15'], // Age 40+
                  requiredCount: 1,
                  subGroup: {
                    ids: [
                      'Q16', // Family history of pancreatic cancer
                      'Q19', // History of pancreatitis
                      'Q11.3', // Persistent non-musculoskeletal upper back pain
                      'Q11.4', // Non-specific abdominal pain
                    ],
                    requiredCount: 1
                  },
                  defaultTier: 1,
                  dependencyRecommendations: [
                    'Suggested bloods: lipase, E/LFTs, FBC,',
                    'IV contrast CT (preferably pancreatic protocol)',
                    'Refer to gastroenterologist or surgeon'
                  ]
                },
                {
                  // Case 2: Age 40+ only
                  ids: ['Q15'], // Age 40+
                  requiredCount: 1,
                  defaultTier: 2,
                  dependencyRecommendations: [
                    'Trial diabetes management for 6 weeks',
                    'If DM not adequately controlled, consider IV contrast abdominal CT (preferably pancreatic protocol)'
                  ]
                }
              ],
              allGroupsRequired: false // We only need one case to be true
            }
          }
        },
        {
          id: 'Q5.3',
          text: 'Stable diabetes mellitus (>12 months)',
          defaultTier: 4,
          recommendations: [],
          dependencyRecommendations: [],
          dependencies: {
            tier1: {
              groups: [
                {
                  ids: [
                    'Q1',
                    'Q7', 
                    'Q3', 
                    'Q9', 
                    'Q4',
                    'Q11.3',
                    'Q11.4',
                    'Q13'
                  ],
                  requiredCount: 2,
                  defaultTier: 3,
                  dependencyRecommendations: [
                    'Investigations of the pancreas should be prioritized if other more common causes are not identified'
                  ]
                },
                {
                  ids: [
                    'Q15',
                    'Q16', 
                    'Q17', 
                    'Q18', 
                    'Q19'
                  ],
                  requiredCount: 0,
                  defaultTier: 3,
                  dependencyRecommendations: [
                    
                  ]
                }
              ],
              allGroupsRequired: true
            }
          }
        }
      ],
      dependencies: {
        tier2: [],
        requiredCount: 0
      }
    },
    { 
      id: 'Q6', 
      text: 'Fever', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier2: [],
        requiredCount: 0
      }
    },
    { 
      id: 'Q7', 
      text: 'Indigestion', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q8', 
      text: 'Jaundice', 
      defaultTier: 1,
      recommendations: [
        'Suggested bloods: lipase, E/LFTs, FBC, CRP, INR',
        'Ultrasound of biliary tree',
        'Refer to a gastroenterologist or surgeon'
      ],
      dependencyRecommendations: [
        'Refer to hospital urgently'
      ],
      dependencies: {
        tier1: {
          groups: [
            {
              ids: ['Q6', 'Q11.1', 'Q11.2', 'Q11.3', 'Q11.4'],
              requiredCount: 1,
              defaultTier: 0,
              dependencyRecommendations: [
                'Refer to hospital urgently'
              ]
            }
          ],
          allGroupsRequired: false
        }
      }
    },
    { 
      id: 'Q9', 
      text: 'Malaise', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q10', 
      text: 'Nausea', 
      defaultTier: 4,
      recommendations: ['Wait no longer than 6 weeks to investigate the pancreas in patients with nausea who are aged 50 or over, for which causes such as infection and new medication have been eliminated.'],
      dependencyRecommendations: [
        'Suggested bloods: lipase, E/LFTs, FBC, coeliac serology',
        'If symptoms persist and other causes not identified IV contrast abdominal CT'
      ],
      dependencies: {
        tier1: {
          groups: [
            {
              ids: ['Q15'], // over 40 years
              requiredCount: 1,
              defaultTier: 2,
              dependencyRecommendations: []
            },
            {
              ids: ['Q11.3', 'Q11.4', 'Q16', 'Q19'],
              requiredCount: 1,
              defaultTier: 2,
              dependencyRecommendations: []
            },
            {
              ids: ['Q11.3', 'Q11.4'],
              requiredCount: 1,
              defaultTier: 2,
              dependencyRecommendations: [
                'Suggested bloods: lipase, E/LFTs, FBC, coeliac serology',
                'If symptoms persist and other causes not identified IV contrast abdominal CT'
              ]
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q11', 
      text: 'Pain', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      subOptions: [
        {
          id: 'Q11.1',
          text: 'Biliary-type pain',
          defaultTier: 2,
          recommendations: ['Suggested bloods: lipase, E/LFTs, FBC, & CRP', 'Ultrasound scan','If gallstones evident: Refer for management','If mass, cyst, or inconclusive findings: IV contrast CT abdominal (within 2 weeks) and refer to a gastroenterologist or surgeon'],
          dependencyRecommendations: [
            'Suggested bloods: lipase, E/LFTs, FBC, & CRP',
            'Ultrasound scan'
          ],
          dependencies: {
            tier1: ['Q11.5'], 
            requiredCount: 1,
            defaultTier: 2
          }
      },
        {
          id: 'Q11.2',
          text: 'Epigastric pain',
          defaultTier: 1,
          recommendations: ['Suggested bloods: lipase, E/LFTs, FBC, & CRP', ' IV contrast abdominal CT (preferably pancreatic protocol)','Refer to gastroenterologist or surgeon'],
          dependencyRecommendations: []
        },
        {
          id: 'Q11.3',
          text: 'Non-musculoskeletal upper back pain',
          defaultTier: 4,
          recommendations: [],
          dependencyRecommendations: ['Investigations of the pancreas should be prioritised if other more common causes are not identified'],
          dependencies: {
            tier1: {
              groups: [
                {
                  ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q11.4', 'Q13', 'Q17', 'Q18'],
                  requiredCount: 2,
                  defaultTier: 3
                }
              ],
              allGroupsRequired: false
            }
          }
        },
        {
          id: 'Q11.4',
          text: 'Non-specific abdominal pain',
          defaultTier: 4,
          recommendations: [],
          dependencyRecommendations: ['Investigations of the pancreas should be prioritised if other more common causes are not identified'],
          dependencies: {
            tier1: {
              groups: [
                {
                  ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q11.3', 'Q13', 'Q17', 'Q18'],
                  requiredCount: 2,
                  defaultTier: 3
                }
              ],
              allGroupsRequired: false
            }
          }
        }
      ]
    },
    { 
      id: 'Q12', 
      text: 'Steatorrhoea', 
      defaultTier: 1,
      recommendations: ['Suggested bloods: lipase, E/LFTs, FBC, CRP, coeliac serology', 'Faecal elastase test','IV contrast abdominal CT (preferably pancreatic protocol)','Refer to gastroenterologist or surgeon'],
      dependencyRecommendations: [],
      dependencies: {
        tier2: [],
        requiredCount: 0
      }
    },
    { 
      id: 'Q13', 
      text: 'Vomiting', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q14', 
      text: 'Weight loss', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [
        'Suggested bloods: lipase, E/LFTs, FBC, thyroid, CRP, iron studies, B12, folate, coagulation profile, HbA1c, coeliac serology',
        'IV contrast abdominal CT abdomen and pelvis',
        'Refer to gastroenterologist or surgeon'
      ],
      dependencies: {
        tier1: {
          groups: [
            {
              ids: ['Q11.3', 'Q11.4', 'Q10', 'Q4'],
              requiredCount: 1,
              defaultTier: 1,
              dependencyRecommendations: [
                'Suggested bloods: lipase, E/LFTs, FBC, thyroid, CRP, iron studies, B12, folate, coagulation profile, HbA1c, coeliac serology',
                'IV contrast abdominal CT abdomen and pelvis',
                'Refer to gastroenterologist or surgeon'
              ]
            }
          ],
          allGroupsRequired: false
        }
      }
    }
  ],
  riskFactors: [
    { 
      id: 'Q15', 
      text: 'Aged over 40 years', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier2: [],
        requiredCount: 0
      }
    },
    { 
      id: 'Q16', 
      text: 'Family history of pancreatic cancer or pancreatic disease', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier1: [],
        requiredCount: 0
      }
    },
    { 
      id: 'Q17', 
      text: 'History of daily smoking within the last 10 years', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q18', 
      text: 'History of regularly consuming 3+ alcoholic drinks per day, within the last 10 years', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier3: {
          groups: [
            {
              ids: ['Q1', 'Q7', 'Q3', 'Q9', 'Q4', 'Q13'],
              requiredCount: 2,
              subGroup: {
                ids: ['Q17', 'Q18'],
                requiredCount: 1
              },
              defaultTier: 3
            }
          ],
          allGroupsRequired: true
        }
      }
    },
    { 
      id: 'Q19', 
      text: 'History of pancreatitis', 
      defaultTier: 4,
      recommendations: [],
      dependencyRecommendations: [],
      dependencies: {
        tier1: [],
        requiredCount: 0
      }
    }
  ]
};

const PancreaticCalculator = () => {
  const [selectedOptions, setSelectedOptions] = useState(new Set());
  const [visibleSubOptions, setVisibleSubOptions] = useState(new Set());
  const [showResults, setShowResults] = useState(false);

  const handleOptionClick = (questionId, subOptionId = null) => {
    const optionKey = subOptionId ? `${questionId}:${subOptionId}` : questionId;
    
    setSelectedOptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(optionKey)) {
        newSet.delete(optionKey);
        // If removing a main option, remove all its sub-options
        if (!subOptionId) {
          Array.from(newSet).forEach(key => {
            if (key.startsWith(`${questionId}:`)) {
              newSet.delete(key);
            }
          });
        }
      } else {
        newSet.add(optionKey);
      }
      return newSet;
    });

    // Handle visibility for main options and their sub-options
    if (!subOptionId) {
      setVisibleSubOptions(prev => {
        const newSet = new Set(prev);
        if (newSet.has(questionId)) {
          newSet.delete(questionId);
        } else {
          newSet.add(questionId);
        }
        return newSet;
      });
    } else {
      // Handle visibility for nested sub-options
      setVisibleSubOptions(prev => {
        const newSet = new Set(prev);
        // When clicking a sub-option that has nested options (like Q11.1)
        if (questionId === 'Q11' && subOptionId === 'Q11.1') {
          if (!newSet.has('Q11.1')) {
            newSet.add('Q11.1');
          } else {
            newSet.delete('Q11.1');
          }
        }
        return newSet;
      });
    }
  };

  const isOptionSelected = (questionId, subOptionId = null) => {
    const optionKey = subOptionId ? `${questionId}:${subOptionId}` : questionId;
    return selectedOptions.has(optionKey);
  };

  const renderSubOptions = (question) => {
    if (!question.subOptions) return null;

    return (
      <div className={`sub-options ${visibleSubOptions.has(question.id) ? 'visible' : ''}`}>
        {question.subOptions.map((subOption) => (
          <div key={subOption.id}>
            <div
              className={`sub-option ${isOptionSelected(question.id, subOption.id) ? 'selected' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleOptionClick(question.id, subOption.id);
              }}
            >
              <span className="plus">+</span>
              <span>{subOption.text}</span>
            </div>
            {subOption.subOptions && (
              <div className={`sub-options nested ${visibleSubOptions.has(subOption.id) ? 'visible' : ''}`}>
                {subOption.subOptions.map((nestedOption) => (
                  <div
                    key={nestedOption.id}
                    className={`sub-option ${isOptionSelected(subOption.id, nestedOption.id) ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(subOption.id, nestedOption.id);
                    }}
                  >
                    <span className="plus">+</span>
                    <span>{nestedOption.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderQuestion = (question) => (
    <div
      key={question.id}
      className={`option ${isOptionSelected(question.id) ? 'selected' : ''} ${visibleSubOptions.has(question.id) ? 'expanded' : ''}`}
      onClick={() => handleOptionClick(question.id)}
    >
      <div className="option-header">
        <label>{question.text}</label>
      </div>
      {question.subOptions && renderSubOptions(question)}
    </div>
  );
  // const renderIntroText = () => (
  //   <div className="intro-text">
  //     <p>This tool has been designed to help streamline investigations for pancreatic cancer.</p>
  //     <p>Please begin by selecting your patient's symptoms and risk factors.</p>
  //     <p>The tool will then guide you through a recommended diagnostic pathway.</p>
  //   </div>
  // );

  const renderRiskFactors = () => (
    <div className="section risk-factors">
      <h2>Select Risk Factors</h2>
      <p>Select all that apply:</p>
      <div className="risk-factors-container">
        {QUESTIONS.riskFactors.map((question) => (
          <div
            key={question.id}
            className={`risk-factor-pill ${isOptionSelected(question.id) ? 'selected' : ''}`}
            onClick={() => handleOptionClick(question.id)}
          >
            {question.text}
          </div>
        ))}
      </div>
    </div>
  );

  if (showResults) {
    return (
      <Results
        selectedOptions={Array.from(selectedOptions)}
        questions={QUESTIONS}
        onClose={() => setShowResults(false)}
      />
    );
  }

  return (
    <div className="calculator-page">
      <div className="calculator-container">
        {/* <img
          src="/images/jpce-logo.png"
          alt="Jreissati Pancreatic Centre and Epworth Logo"
          className="logo"
        />
        <h1>Decision support tool for the detection of pancreatic cancer</h1>
        {renderIntroText()} */}
        <div className="tier" data-tier="1">
          <h2>Select Symptoms</h2>
          <p>Select all that apply:</p>
          <div className="options">
            {QUESTIONS.symptoms.map(renderQuestion)}
          </div>
        </div>

        {renderRiskFactors()}

        <div className="button-container">
          <div className="button-wrapper">
            <button onClick={() => setShowResults(true)}>
              Submit
            </button>
          </div>
          <div className="button-wrapper">
            <button 
              className="secondary"
              onClick={() => setSelectedOptions(new Set())}
            >
              Reset selections
            </button>
          </div>
        </div>
        <div className="citation-text">
            This tool has been adapted from: Thompson B, Philcox S, Devereaux B, Metz A, Croagh D, Windsor J, Davaris A, Gupta S, Barlow J, Rhee J, Tagkalidis P, Zimet A, Sharma A, Manocha R, Neale RE. A decision support tool for the detection of pancreatic cancer in general practice: A modified Delphi consensus. Pancreatology. 2021 Aug 18:S1424-3903(21)00530-5. doi: 10.1016/j.pan.2021.08.007. PMID: 34483054.
          </div>
          
          <div className="funders-section">
            <p>This tool was made possible with the support of:</p>
            <div className="funders-logos">
              <div className="funder-logo">
                <img src="https://emf.org.au/-/media/project/epworth/epworthweb/logos/giving-header/emf-logo-emf.png?h=209&w=372&hash=F3C17522BFE5AF9353D22FA0789E8D6D" alt="Epworth Medical Foundation" />
              </div>
              <div class="funder-logo">
                <img src="https://images.squarespace-cdn.com/content/v1/69800257724fc67d6c0bdc10/ebc75866-8a7c-42af-b841-be05aab41c40/Logo+1.png?format=1500w" alt="Telematics Trust" />
              </div>
            </div>
            <div className="gp-reviewed-section">
              <i className="far fa-thumbs-up gp-reviewed-icon"></i>
              <span>GP reviewed 2025</span>
            </div>
          </div>
      </div>
    </div>
  );
};

export default PancreaticCalculator;
